import { tr } from "../../i18n";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Circle, Eye, Download } from "lucide-react";
import { api } from "../../auth/AuthContext";
import { Badge, statusTone, Card, EmptyState, fmtDate, fileUrl } from "../../components/portal";
const SUMMARY_LABELS = {
  assessment_summary: "Assessment Summary",
  eri_results: "ERI Results",
  key_findings: "Key Findings",
  risk_summary: "Risk Summary",
  recommendations: "Recommendations",
  management_summary: "Management Summary",
  additional_notes: "Additional Notes"
};
function EriResultView({
  id
}) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    api.get(`/client/eri/${id}`).then(r => setDoc(r.data)).catch(() => setDoc(false));
  }, [id]);
  if (doc === null) return <p className="text-sm text-esaia-subtle">{tr("Loading...")}</p>;
  if (doc === false) return <EmptyState message={tr("You do not have permission to access this resource.")} />;
  return <div className="space-y-6">
      <Card title={tr("ERI Result")} testid="eri-result-card">
        <div className="flex flex-wrap items-center gap-6">
          <p className="font-serif text-5xl font-light text-esaia-blue">{doc.total_score}<span className="text-xl text-esaia-subtle">/100</span></p>
          <Badge tone={doc.band === "Established" ? "green" : doc.band === "Developing" ? "amber" : "red"}>{tr(doc.band)}</Badge>
          <p className="text-xs text-esaia-subtle">{tr("Completed")}{" "}{fmtDate(doc.created_at)}</p>
        </div>
        <div className="mt-6 space-y-4">
          {Object.entries(doc.section_scores || {}).map(([k, v]) => <div key={k}>
              <div className="flex justify-between text-sm">
                <span className="font-medium text-esaia-ink">{tr(k)}</span>
                <span className="font-mono text-xs text-esaia-subtle">{v}%</span>
              </div>
              <div className="mt-1.5 h-2 w-full bg-esaia-line">
                <div className={`h-2 ${v >= 70 ? "bg-emerald-600" : v >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{
              width: `${v}%`
            }} />
              </div>
            </div>)}
        </div>
      </Card>
      {(doc.attention || []).length > 0 && <Card title={tr("Key Attention Areas")} testid="eri-attention-card">
          <ul className="space-y-3">
            {doc.attention.map((a, i) => <li key={i} className="flex items-start gap-3 text-sm text-esaia-muted">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-red-500" />
                <span><span className="font-medium text-esaia-ink">{a.section}:</span> {a.text}</span>
              </li>)}
          </ul>
        </Card>}
    </div>;
}
export default function AssessmentDetail() {
  const {
    lang = "en",
    id
  } = useParams();
  const isEri = id?.startsWith("eri-");
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    if (!isEri) {
      api.get(`/client/assessments/${id}`).then(r => setDoc(r.data)).catch(() => setDoc(false));
    }
  }, [id, isEri]);
  return <div className="mx-auto max-w-5xl" data-testid="client-assessment-detail">
      <Link to={`/${lang}/portal/assessments`} className="group inline-flex items-center gap-2 text-xs font-semibold text-esaia-blue" data-testid="back-to-assessments">
        <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />{" "}{tr("Back to Assessments")}{" "}</Link>

      {isEri ? <div className="mt-6">
          <h1 className="mb-6 text-2xl font-semibold tracking-tight text-esaia-ink">{tr("Ethical Readiness Index")}</h1>
          <EriResultView id={id.slice(4)} />
        </div> : doc === null ? <p className="mt-8 text-sm text-esaia-subtle">{tr("Loading...")}</p> : doc === false ? <div className="mt-8"><EmptyState message={tr("You do not have permission to access this resource.")} /></div> : <div className="mt-6 space-y-6">
          <div className="border border-esaia-line bg-white p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-semibold text-esaia-ink sm:text-2xl">{tr(doc.service_label)}</h1>
                <p className="mt-1 font-mono text-xs text-esaia-subtle">{doc.assessment_id}</p>
              </div>
              <Badge tone={statusTone(doc.status)} testid="assessment-status-badge">{tr(doc.status)}</Badge>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-5 border-t border-esaia-line pt-6 sm:grid-cols-4">
              {[["Start Date", fmtDate(doc.start_date)], ["Completion Date", fmtDate(doc.completion_date)], ["Last Updated", fmtDate(doc.updated_at)], ["Assessment ID", doc.assessment_id]].map(([k, v]) => <div key={k}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{k}</p>
                  <p className="mt-1.5 text-sm font-medium text-esaia-ink">{v}</p>
                </div>)}
            </div>
          </div>

          {Object.keys(doc.summaries || {}).length > 0 && <Card title={tr("Assessment Summary")} testid="assessment-summaries">
              <div className="space-y-6">
                {Object.entries(doc.summaries).map(([f, text]) => <div key={f}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-blue">{SUMMARY_LABELS[f] || f}</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-esaia-muted">{text}</p>
                  </div>)}
              </div>
            </Card>}

          <Card title={tr("Assessment Progress")} testid="assessment-checklist">
            <ul className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {(doc.checklist || []).map((c, i) => <li key={i} className="flex items-center gap-3 text-sm" data-testid={`checklist-item-${i}`}>
                  {c.completed ? <Check size={16} className="shrink-0 text-emerald-600" /> : <Circle size={15} className="shrink-0 text-esaia-line" />}
                  <span className={c.completed ? "text-esaia-ink" : "text-esaia-subtle"}>{c.item}</span>
                </li>)}
            </ul>
          </Card>

          <Card title={tr("Management Reports")} testid="assessment-reports">
            {(doc.reports || []).length === 0 ? <p className="text-sm text-esaia-subtle">{tr("No management reports have been uploaded for this assessment.")}</p> : <ul className="divide-y divide-esaia-line">
                {doc.reports.map(r => <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`report-${r.id}`}>
                    <div>
                      <p className="text-sm font-medium text-esaia-ink">{r.file_name}</p>
                      <p className="text-xs text-esaia-subtle">{fmtDate(r.uploaded_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      <a href={fileUrl("reports", r.id, true)} target="_blank" rel="noreferrer" data-testid={`report-view-${r.id}`} className="inline-flex items-center gap-1.5 border border-esaia-blue/40 px-3.5 py-2 text-xs font-semibold text-esaia-blue transition-colors hover:bg-esaia-blue hover:text-white">
                        <Eye size={14} />{" "}{tr("View")}{" "}</a>
                      <a href={fileUrl("reports", r.id)} data-testid={`report-download-${r.id}`} className="inline-flex items-center gap-1.5 bg-esaia-blue px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-esaia-dark">
                        <Download size={14} />{" "}{tr("Download")}{" "}</a>
                    </div>
                  </li>)}
              </ul>}
          </Card>

          {(doc.certificates || []).length > 0 && <Card title={tr("Certificates")} testid="assessment-certificates">
              <ul className="divide-y divide-esaia-line">
                {doc.certificates.map(c => <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5">
                    <div>
                      <p className="text-sm font-medium text-esaia-ink">{c.certificate_number}</p>
                      <p className="text-xs text-esaia-subtle">{fmtDate(c.start_date)} — {fmtDate(c.expiry_date)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge tone={c.effective_status === "ACTIVE" ? "green" : "red"}>{tr(c.effective_status)}</Badge>
                      <a href={fileUrl("certificates", c.id, true)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-esaia-blue/40 px-3.5 py-2 text-xs font-semibold text-esaia-blue hover:bg-esaia-blue hover:text-white">
                        <Eye size={14} />{" "}{tr("View")}{" "}</a>
                    </div>
                  </li>)}
              </ul>
            </Card>}
        </div>}
    </div>;
}
